import {
  stripColorVisionFilterFromClone,
  withColorVisionFilterDisabled,
} from "./colorVisionFilters";

export const PDF_PAGE_WIDTH_MM = 210;
export const PDF_PAGE_HEIGHT_MM = 297;
export const PDF_JPEG_QUALITY = 0.82;
export const PDF_CAPTURE_SCALE = 1.5;
export const PDF_DOCUMENT_WIDTH_PX = 600;

export const PDF_SIG_DATE_X_MM = 10;
export const PDF_SIG_DATE_W_MM = 60;
export const PDF_SIG_BOX_X_MM = 72;
export const PDF_SIG_BOX_Y_TOP_MM = 242;
export const PDF_SIG_BOX_W_MM = 128;
export const PDF_SIG_BOX_H_MM = 25;
export const PDF_SIG_LABEL_HEIGHT_MM = 5;
export const PDF_SIG_CONTENT_GAP_MM = 2;

export function stripEditorFocusRingFromClone(clonedDocument) {
  clonedDocument.querySelectorAll(".ProseMirror").forEach((element) => {
    element.style.outline = "none";
    element.style.boxShadow = "none";
  });
}

export const HTML2CANVAS_PDF_OPTIONS = {
  scale: PDF_CAPTURE_SCALE,
  useCORS: true,
  logging: false,
  backgroundColor: "#ffffff",
  onclone: (clonedDocument) => {
    stripColorVisionFilterFromClone(clonedDocument);
    stripEditorFocusRingFromClone(clonedDocument);
  },
};

export function html2canvasForPdf(html2canvas, element, options = {}) {
  const { onclone, ...rest } = options;
  return withColorVisionFilterDisabled(() =>
    html2canvas(element, {
      ...HTML2CANVAS_PDF_OPTIONS,
      ...rest,
      onclone: (clonedDocument, clonedElement) => {
        stripColorVisionFilterFromClone(clonedDocument);
        stripEditorFocusRingFromClone(clonedDocument);
        onclone?.(clonedDocument, clonedElement);
      },
    }),
  );
}

/**
 * Força largura CSS fixa no root da prévia durante a captura.
 * Assim header/conteúdo/footer layoutam em PDF_DOCUMENT_WIDTH_PX mesmo com
 * zoom do navegador ou janela estreita (width:100% + maxWidth encolhia antes).
 */
export async function withFixedPdfCaptureWidth(
  element,
  callback,
  widthPx = PDF_DOCUMENT_WIDTH_PX,
) {
  if (typeof callback !== "function") {
    return undefined;
  }
  if (!element) {
    return callback();
  }

  const previousInline = {
    width: element.style.width,
    maxWidth: element.style.maxWidth,
    minWidth: element.style.minWidth,
    boxSizing: element.style.boxSizing,
    flexShrink: element.style.flexShrink,
  };

  element.style.boxSizing = "border-box";
  element.style.width = `${widthPx}px`;
  element.style.maxWidth = `${widthPx}px`;
  element.style.minWidth = `${widthPx}px`;
  element.style.flexShrink = "0";
  void element.offsetWidth;

  try {
    return await callback();
  } finally {
    element.style.width = previousInline.width;
    element.style.maxWidth = previousInline.maxWidth;
    element.style.minWidth = previousInline.minWidth;
    element.style.boxSizing = previousInline.boxSizing;
    element.style.flexShrink = previousInline.flexShrink;
  }
}

const BLANK_ROW_EDGE_IGNORE_RATIO = 0.1;
const BLANK_ROW_DARKNESS_THRESHOLD = 245;
const BLANK_ROW_MAX_DARK_RATIO = 0.015;

export function canvasHeightInMm(
  canvas,
  pageWidthMm = PDF_PAGE_WIDTH_MM,
) {
  if (!canvas?.width) return 0;
  return (canvas.height * pageWidthMm) / canvas.width;
}

export function isMostlyBlankRow(
  imageData,
  width,
  {
    edgeIgnoreRatio = BLANK_ROW_EDGE_IGNORE_RATIO,
    darknessThreshold = BLANK_ROW_DARKNESS_THRESHOLD,
    maxDarkRatio = BLANK_ROW_MAX_DARK_RATIO,
  } = {},
) {
  const data = imageData.data;
  const startX = Math.floor(width * edgeIgnoreRatio);
  const endX = Math.ceil(width * (1 - edgeIgnoreRatio));
  let darkCount = 0;
  let sampled = 0;

  for (let x = startX; x < endX; x += 1) {
    const index = x * 4;
    const red = data[index];
    const green = data[index + 1];
    const blue = data[index + 2];
    sampled += 1;
    if (
      red < darknessThreshold ||
      green < darknessThreshold ||
      blue < darknessThreshold
    ) {
      darkCount += 1;
    }
  }

  return sampled > 0 && darkCount / sampled <= maxDarkRatio;
}

export function findSafeCutY(
  contentCanvas,
  idealEndY,
  minY,
  searchWindowPx,
) {
  const maxY = Math.min(Math.floor(idealEndY), contentCanvas.height - 1);
  const lowerBound = Math.max(Math.ceil(minY), maxY - Math.max(1, Math.floor(searchWindowPx)));

  if (maxY <= lowerBound) {
    return Math.max(Math.ceil(minY), Math.min(maxY, contentCanvas.height));
  }

  const ctx = contentCanvas.getContext("2d", { willReadFrequently: true });
  if (!ctx || typeof ctx.getImageData !== "function") {
    return maxY;
  }

  for (let y = maxY; y >= lowerBound; y -= 1) {
    try {
      const row = ctx.getImageData(0, y, contentCanvas.width, 1);
      if (isMostlyBlankRow(row, contentCanvas.width)) {
        return y;
      }
    } catch {
      return maxY;
    }
  }

  return maxY;
}

export function buildContentPageSlices(
  contentCanvas,
  contentAreaHeightPx,
  options = {},
) {
  const searchWindowPx =
    options.searchWindowPx ??
    Math.max(64, Math.round(contentAreaHeightPx * 0.12));

  const slices = [];
  let sourceY = 0;

  while (sourceY < contentCanvas.height) {
    const remaining = contentCanvas.height - sourceY;
    if (remaining <= contentAreaHeightPx) {
      slices.push({ sourceY, height: remaining });
      break;
    }

    const idealEndY = sourceY + contentAreaHeightPx;
    const cutY = findSafeCutY(
      contentCanvas,
      idealEndY,
      sourceY + Math.max(24, Math.floor(contentAreaHeightPx * 0.35)),
      searchWindowPx,
    );

    const height = Math.max(1, cutY - sourceY);
    slices.push({ sourceY, height });
    sourceY += height;
  }

  return slices.length > 0
    ? slices
    : [{ sourceY: 0, height: Math.max(1, contentCanvas.height) }];
}

export function getContentPaging(
  contentCanvas,
  headerHeightMm,
  footerHeightMm,
  pageWidthMm = PDF_PAGE_WIDTH_MM,
  pageHeightMm = PDF_PAGE_HEIGHT_MM,
  options = {},
) {
  const contentHeightMm = canvasHeightInMm(contentCanvas, pageWidthMm);
  const signatureBoxYTopMm = options.signatureBoxYTopMm;
  const signatureContentGapMm = options.signatureContentGapMm ?? PDF_SIG_CONTENT_GAP_MM;

  let contentAreaHeightMm;
  if (signatureBoxYTopMm != null && Number.isFinite(signatureBoxYTopMm)) {
    contentAreaHeightMm = Math.max(
      1,
      signatureBoxYTopMm - headerHeightMm - signatureContentGapMm,
    );
  } else {
    contentAreaHeightMm = Math.max(
      1,
      pageHeightMm - headerHeightMm - footerHeightMm,
    );
  }

  const pxPerMm = contentCanvas.width / pageWidthMm;
  const contentAreaHeightPx = Math.ceil(contentAreaHeightMm * pxPerMm);
  const slices = buildContentPageSlices(contentCanvas, contentAreaHeightPx);
  const lastSlice = slices[slices.length - 1];
  const lastSliceHeightMm = canvasHeightInMm(
    { width: contentCanvas.width, height: lastSlice.height },
    pageWidthMm,
  );

  return {
    contentHeightMm,
    contentAreaHeightMm,
    totalPages: slices.length,
    pxPerMm,
    contentAreaHeightPx,
    slices,
    lastSliceHeightMm,
  };
}

export function sliceCanvasVertically(sourceCanvas, sourceY, sliceHeight) {
  const remaining = Math.max(0, sourceCanvas.height - Math.max(0, sourceY));
  const height = Math.max(1, Math.min(Math.ceil(sliceHeight), remaining || 1));
  const slice = document.createElement("canvas");
  slice.width = sourceCanvas.width;
  slice.height = height;
  const ctx = slice.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, slice.width, slice.height);
  if (remaining > 0) {
    ctx.drawImage(
      sourceCanvas,
      0,
      Math.max(0, sourceY),
      sourceCanvas.width,
      height,
      0,
      0,
      sourceCanvas.width,
      height,
    );
  }
  return slice;
}

export function canvasToJpegDataUrl(canvas, quality = PDF_JPEG_QUALITY) {
  const withBackground = document.createElement("canvas");
  withBackground.width = canvas.width;
  withBackground.height = canvas.height;
  const ctx = withBackground.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, withBackground.width, withBackground.height);
  ctx.drawImage(canvas, 0, 0);
  return withBackground.toDataURL("image/jpeg", quality);
}

export function addSlicedContentImage(
  pdf,
  contentCanvas,
  pageIndex,
  paging,
  pageWidthMm = PDF_PAGE_WIDTH_MM,
  headerHeightMm,
) {
  const pageSlice = paging.slices?.[pageIndex];
  if (!pageSlice) return null;

  const slice = sliceCanvasVertically(
    contentCanvas,
    pageSlice.sourceY,
    pageSlice.height,
  );
  const dataUrl = canvasToJpegDataUrl(slice);
  const sliceHeightMm = canvasHeightInMm(slice, pageWidthMm);
  pdf.addImage(
    dataUrl,
    "JPEG",
    0,
    headerHeightMm,
    pageWidthMm,
    sliceHeightMm,
  );
  return { dataUrl, sliceHeightMm };
}
